import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Placement } from '@popperjs/core';
import { useMergedRefs } from '../hooks';
import { DropdownParentContext } from './DropdownParentContext';
import { useDropdownLogic } from './useDropdownLogic';
import { menuMotionMs } from './Menu.styled';

type DropdownMenuOptions<E extends HTMLElement> = {
	onMenuOpen?: () => unknown;
	onMenuClose?: () => unknown;
	placement?: Placement;
	/** The menu takes the trigger's width. See useDropdownLogic. */
	sticky?: boolean;
	/** The ref a forwarding trigger has to keep, merged into ours. */
	triggerRef?: React.ForwardedRef<E>;
	/** Runs once the collapse has played and the menu is gone. */
	onCollapsed?: () => void;
};

/**
 * Everything a Menu-backed dropdown needs that is not its trigger or its rows:
 * the popper, the open state, the mount that outlives it, and the portal.
 *
 * Every dropdown in the library is built on this, so a change to how menus
 * open, animate, or portal is made once here rather than in each of them.
 * `useDropdownLogic` remains the layer below, for a popover that is not a Menu.
 */
export const useDropdownMenu = <E extends HTMLElement = HTMLElement>({
	triggerRef: externalRef,
	onCollapsed,
	...logic
}: DropdownMenuOptions<E> = {}) => {
	const {
		isOpen, styles, attributes, onOpen, onClose, onToggle, buttonRef, menuRef,
	} = useDropdownLogic(logic);

	// The menu has to outlive isOpen so its collapse can play. A timer rather
	// than animationend: the event never arrives from a portalled node whose
	// animation is interrupted, or on a backgrounded tab, and a menu that never
	// unmounts is worse than one that unmounts a frame late.
	const [menuMounted, setMenuMounted] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setMenuMounted(true);
			return;
		}
		const collapsed = setTimeout(() => {
			setMenuMounted(false);
			// Whatever the caller resets here — a search phrase, say — outlives the
			// menu by one animation. Clearing it on close instead would swap the
			// filtered list for the full one mid-collapse.
			onCollapsed?.();
		}, menuMotionMs);

		return () => clearTimeout(collapsed);
	// isOpen is the only trigger; onCollapsed is re-made every render at most
	// call sites, and listing it would restart the timer on every keystroke.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const { parent } = useContext(DropdownParentContext);

	return {
		isOpen,
		onOpen,
		onClose,
		onToggle,
		/** Goes on the trigger — already merged with any forwarded ref. */
		triggerRef: useMergedRefs<E>(buttonRef, externalRef ?? null),
		/** Spread onto the Menu: where it sits, its ref, and which way it animates. */
		menuProps: {
			popperStyles: styles,
			popperAttributes: attributes,
			ref: menuRef,
			closing: !isOpen,
		},
		/** Portals the menu into the dropdown's parent, or nothing while it is gone. */
		renderMenu: (menu: React.ReactNode) =>
			menuMounted ? createPortal(menu, parent || document.body) : null,
	};
};
